import React, { useState, useEffect } from "react";
import './EventTypesSetup.css';

function EventCategories() {
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEventType, setCurrentEventType] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    code: "",
    displayName: ""
  });

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('authToken');
  };

  // Fetch all event types
  const fetchEventTypes = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch("https://api.asterickx.com/api/EventTypes", {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setEventTypes(data);
    } catch (error) {
      console.error("Error fetching event types:", error);
      alert("Failed to load event types. Please check your authentication.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventTypes();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddNew = () => {
    setEditMode(false);
    setCurrentEventType(null);
    setFormData({
      code: "",
      displayName: ""
    });
    setShowModal(true);
  };

  const handleEdit = (eventType) => {
    setEditMode(true);
    setCurrentEventType(eventType);
    setFormData({
      code: eventType.code || "",
      displayName: eventType.displayName || ""
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.displayName) {
      alert("Code and Display Name are required");
      return;
    }
    
    const token = getAuthToken();
    
    if (!token) {
      alert("Authentication token not found. Please log in again.");
      return;
    }
    
    const dataToSend = {
      code: formData.code,
      displayName: formData.displayName
    };

    try {
      let response;
      if (editMode && currentEventType) {
        response = await fetch(
          `https://api.asterickx.com/api/EventTypes/${currentEventType.code}`,
          {
            method: "PUT",
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(dataToSend)
          }
        );
      } else {
        response = await fetch(
          "https://api.asterickx.com/api/EventTypes",
          {
            method: "POST",
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(dataToSend)
          }
        );
      }

      if (response.ok) {
        alert(editMode ? "Event type updated successfully!" : "Event type added successfully!");
        setShowModal(false);
        fetchEventTypes();
      } else {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        alert(`Failed to save event type: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error saving event type:", error);
      alert("An error occurred while saving");
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm("Are you sure you want to delete this event type?")) {
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(
        `https://api.asterickx.com/api/EventTypes/${code}`,
        {
          method: "DELETE",
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        alert("Event type deleted successfully!");
        fetchEventTypes();
      } else {
        alert("Failed to delete event type");
      }
    } catch (error) {
      console.error("Error deleting event type:", error);
      alert("An error occurred while deleting");
    }
  };

  const filteredEventTypes = eventTypes.filter(eventType =>
    eventType.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    eventType.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="ec-container">
      {/* Header Section */}
      <div className="ec-header-section">
        <h1 className="ec-page-title">Event Categories Management</h1>
        <div className="ec-right-controls">
          <div className="ec-search-container">
            <input
              type="text"
              placeholder="Search event types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ec-search-input"
            />
            <span className="ec-search-icon">🔍</span>
          </div>
          <button className="ec-add-btn" onClick={handleAddNew}>
            <span className="ec-btn-icon">+</span>
            Add New Type
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="ec-text-center">
          <div className="ec-loader"></div>
          <p>Loading event types...</p>
        </div>
      ) : (
        <div className="ec-grid-container">
          {filteredEventTypes.length === 0 ? (
            <div className="ec-empty-state">
              <div className="ec-empty-icon">📋</div>
              <h3>No event types found</h3>
              <p>Start by adding a new event type</p>
            </div>
          ) : (
            <div className="ec-grid">
              {filteredEventTypes.map((eventType) => (
                <div key={eventType.code} className="ec-card">
                  <div className="ec-card-header">
                    <h3 className="ec-card-title">{eventType.displayName}</h3>
                    <div className="ec-card-actions">
                      <button 
                        className="ec-edit-icon-btn" 
                        onClick={() => handleEdit(eventType)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        className="ec-delete-icon-btn" 
                        onClick={() => handleDelete(eventType.code)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="ec-card-body">
                    <p className="ec-card-description">
                      <strong>Code:</strong> {eventType.code}
                    </p>
                  </div>
                  <div className="ec-card-footer">
                    <span className="ec-card-id">Type Code: {eventType.code}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="ec-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ec-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="ec-modal-header">
              <h3>{editMode ? "Edit Event Type" : "Add New Event Type"}</h3>
              <button className="ec-close-btn" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className="ec-modal-body">
              <div className="ec-form-grid">
                <div className="ec-form-group ec-full-width">
                  <label>Code *</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="Enter event type code (e.g., CONFERENCE)"
                    disabled={editMode}
                  />
                  {editMode && (
                    <small style={{ color: '#999', fontSize: '12px', marginTop: '4px' }}>
                      Code cannot be changed when editing
                    </small>
                  )}
                </div>

                <div className="ec-form-group ec-full-width">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    placeholder="Enter display name (e.g., Conference)"
                  />
                </div>
              </div>

              <div className="ec-form-actions">
                <button className="ec-cancel-btn" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button className="ec-submit-btn" onClick={handleSubmit}>
                  {editMode ? "Update" : "Add"} Event Type
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventCategories;